/**
 * `AW15` · Cómo se ROTULA lo que la fila del padrón trae, en un solo lugar.
 *
 * Es presentación y nada más: no decide qué campos cruzan (eso es `ADR-0191`) ni
 * qué se pide (eso es `borde-padron.ts`). Vive aparte —mismo precedente que
 * `conversaciones/formato.ts`— porque los mismos rótulos los usan la TABLA y el
 * SELECTOR de filtros, y dos copias divergirían sin que nada avise: el operador
 * vería un lote llamado de una forma en el selector y de otra en la columna.
 */

/**
 * ⭐ **EL LOTE VACÍO ES UN ESTADO LEGÍTIMO, Y ACÁ SE DECIDE CÓMO SE LEE.**
 *
 * `modules/participants` usa la cadena vacía para el alta manual —«un alta
 * manual no proviene de ningún batch, y la cadena vacía es la única forma de
 * decirlo que no inventa un nombre de lote que ninguna fuente fija»—, y
 * `ADR-0181` midió el precio de tratarla como un hueco: «todo filtro por lote
 * las pierde a las 296».
 *
 * `ADR-0191` §Pata ③ decide que el dato CRUZA y deja explícitamente el rótulo
 * afuera (§Lo que este ADR NO decide, fila **e**: *«que se muestre como “alta
 * manual”, como guion o como celda vacía es decisión de producto»*, dueño
 * `web-dev`, «cuando la pantalla exista»). **La pantalla existe ahora y ésta es
 * la decisión**, con su motivo:
 *
 *   · **celda vacía** queda descartada porque se lee como «no sé» —el estado que
 *     `estilos.css` §Los tres estados separa a propósito de «no hay»— y acá el
 *     dato está y es completo;
 *   · **un guion** dice lo mismo con otro glifo;
 *   · ~~«Alta manual»~~ **quedó descartado el `[29/9/2026]` por `R3-UI3`**: el
 *     PRD manda que la celda del alta manual se lea «—» y que NINGÚN texto de la
 *     pantalla diga «Alta manual» (`gates/r1/R3-UI3-pantalla.gate.ts`,
 *     `tabla-vacia-texto-y-sin-alta-manual`). El rótulo del lote convive con el
 *     filtro de lotes y nombrar el alta manual ahí adentro haría que la pantalla
 *     lo dijera dos veces.
 *
 * ⛔ No es un nombre de lote inventado: es un rótulo de la PANTALLA sobre un
 * valor que sigue siendo `""` en el cable y en el filtro. Lo que se manda al
 * borde cuando el operador lo elige es la cadena vacía, no este texto.
 */
export const ROTULO_DE_ALTA_MANUAL = "—"

/** El rótulo de un lote, con el caso del alta manual resuelto. */
export function rotuloDeLote(lote: string): string {
  return lote === "" ? ROTULO_DE_ALTA_MANUAL : lote
}

/**
 * El rótulo de un flag.
 *
 * Texto y no un ícono ni un color: la columna se lee con un lector de pantalla y
 * en blanco y negro, y un check verde contra una celda vacía no distingue «no» de
 * «no lo sé». Los cuatro flags son `boolean` en el contrato —no hay tercer
 * estado—, así que las dos palabras cubren el dominio entero.
 */
export function rotuloDeBandera(valor: boolean): string {
  return valor ? "Sí" : "No"
}
